import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { usePlan } from "contexts/plan";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import {
  MaterialRead,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { getDriMass } from "./utils";

interface Props {
  planId: PlanId;
  period: Period;
  materials: MaterialRead[];
}
interface BodyProps {
  planId: PlanId;
  period: Period;
  driMaterialId: number;
}

const Body = ({ planId, period, driMaterialId }: BodyProps) => {
  const [, summarisePlanResultsMutationResult] =
    useSummarisePlanResultsMutation({
      fixedCacheKey: `${planId}-${period}`,
    });
  const { plan } = usePlan(planId);
  const { format } = useNumberSerialiser({ decimalPlaces: 0 });
  const units = useUnitsFormatter(false);

  switch (summarisePlanResultsMutationResult.status) {
    case QueryStatus.rejected:
    case QueryStatus.uninitialized:
      return null;
    case QueryStatus.pending:
      return <Skeleton width={25} />;
    case QueryStatus.fulfilled: {
      if (plan.status !== "success") {
        <Skeleton width={25} />;
      } else {
        const thisPlanConsumed =
          summarisePlanResultsMutationResult.data.mixes.reduce(
            (total, result) => {
              const numberOfHeats = result.production.reduce(
                (sum, item) => sum + item.num_heats,
                0
              );
              return total + getDriMass(result.mix) * numberOfHeats;
            },
            0
          );
        const planStartConsumed =
          plan.data.summary.period_material_summary.find(
            (summary) =>
              summary.period === 1 && summary.material_id === driMaterialId
          )?.local_month_to_date_consumed ?? 0;
        const formattedQuantity = format(thisPlanConsumed + planStartConsumed);

        return (
          <Typography
            variant="body1Mono"
            sx={{ fontSize: "1rem" }}
          >{`${formattedQuantity}${units("mass", true)}`}</Typography>
        );
      }
    }
  }
};
export const DriMonthToDateConsumption = ({
  planId,
  period,
  materials,
}: Props) => {
  const driMaterialId = materials.find(
    (material) => material.addition_location === "hopper"
  )?.id;
  const { t } = useTenantTranslation();

  if (driMaterialId === undefined) {
    return <Box />;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 2 }}>
      <Typography variant="h5" noWrap>{`${t(
        "monthToDateDriConsumed"
      )}: `}</Typography>
      <Body planId={planId} period={period} driMaterialId={driMaterialId} />
    </Stack>
  );
};
